import React from "react";
import PropTypes from "prop-types";
import { Drawer, Radio, DatePicker, Select, Button, Icon, message } from "antd";
import moment from "moment";
import { CaseService } from "../../services";
import "styles/account-statement-drawer.css";

const RadioGroup = Radio.Group;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DATE_RANGE_OPTIONS = [
    { value: "all", label: "All" },
    { value: "1", label: "1 Month" },
    { value: "3", label: "3 Months" },
    { value: "custom", label: "Custom" },
];

const FILTER_OPTIONS = [
    { value: "buyer", label: "Buyer Wise" },
    { value: "case", label: "Case Wise" },
];

const INITIAL_STATE = {
    dateFilter: null,
    startDate: null,
    endDate: null,
    filterBy: undefined,
    itemId: undefined,
    downloading: false,
};

class AccountStatementDrawer extends React.Component {
    state = { ...INITIAL_STATE };

    componentDidUpdate(prevProps) {
        // Reset the form whenever the drawer transitions from closed -> open
        if (this.props.visible && !prevProps.visible) {
            this.setState({ ...INITIAL_STATE });
        }
    }

    get idOptions() {
        const { filterBy } = this.state;
        const { buyerId, caseId, caseIds } = this.props;
        if (filterBy === "buyer") {
            return buyerId ? [buyerId] : [];
        }
        if (filterBy === "case") {
            if (caseIds && caseIds.length) {
                return caseIds;
            }
            return caseId ? [caseId] : [];
        }
        return [];
    }

    onChangeDateFilter = e => {
        const value = e.target.value;
        if (value === "1" || value === "3") {
            this.setState({
                dateFilter: value,
                startDate: moment().subtract(Number(value), "months").format("YYYY-MM-DD"),
                endDate: moment().format("YYYY-MM-DD"),
            });
        } else {
            this.setState({ dateFilter: value, startDate: null, endDate: null });
        }
    };

    onChangeCustomRange = value => {
        if (!value || !value.length) {
            this.setState({ startDate: null, endDate: null });
            return;
        }
        this.setState({
            startDate: value[0].format("YYYY-MM-DD"),
            endDate: value[1].format("YYYY-MM-DD"),
        });
    };

    onChangeFilterBy = filterBy => {
        // Buyer-wise has a single id -> auto-select it.
        // Case-wise can have multiple ids -> let the user pick.
        const { buyerId } = this.props;
        const itemId = filterBy === "buyer" ? (buyerId || undefined) : undefined;
        this.setState({ filterBy, itemId });
    };

    onChangeItemId = itemId => this.setState({ itemId });

    handleDownload = () => {
        const { dateFilter, startDate, endDate, filterBy, itemId } = this.state;

        if (!dateFilter) {
            message.warning("Please select a date range");
            return;
        }
        if (!filterBy) {
            message.warning("Please select filter by");
            return;
        }
        if (!itemId) {
            message.warning("Please select an ID");
            return;
        }

        const payload = {
            start_date: startDate,
            end_date: endDate,
        };
        if (filterBy === "buyer") {
            payload.buyer_id = itemId;
        } else {
            payload.case_id = itemId;
        }

        this.setState({ downloading: true });
        CaseService.downloadRetailAccountStatement(payload)
            .then(res => {
                message.success(
                    (res && res.data && res.data.data) ||
                    "Requested report has been sent over email. It'll be received shortly",
                    7
                );
                this.props.onClose && this.props.onClose();
            })
            .catch(err => {
                const data = (err && err.data) || err || {};
                message.error(
                    data.detail || data.data || "Error / No Data! Report can't be downloaded"
                );
            })
            .finally(() => {
                this.setState({ downloading: false });
            });
    };

    render() {
        const { visible, onClose, caseId, buyerId } = this.props;
        const { dateFilter, filterBy, itemId, downloading } = this.state;

        return (
            <Drawer
                className="account-statement-drawer"
                title={null}
                width={440}
                placement="right"
                closable={false}
                onClose={onClose}
                visible={visible}
                destroyOnClose
            >
                <div className="acc-stmt-header">
                    <div className="d-flex justify-content-space-between align-items-start">
                        <div className="acc-stmt-header-title">
                            <span className="acc-stmt-header-icon">
                                <Icon type="file-text" />
                            </span>
                            <div>
                                <h3>Account Statement</h3>
                                <p>Generate &amp; email the account statement report</p>
                            </div>
                        </div>
                        <Button
                            type="link"
                            icon="close"
                            onClick={onClose}
                            className="acc-stmt-close"
                        />
                    </div>
                    <div className="acc-stmt-meta">
                        <span className="acc-stmt-meta-chip">
                            <Icon type="profile" /> Case&nbsp;ID: <strong>{caseId || "--"}</strong>
                        </span>
                        <span className="acc-stmt-meta-chip">
                            <Icon type="user" /> Buyer&nbsp;ID: <strong>{buyerId || "--"}</strong>
                        </span>
                    </div>
                </div>

                <div className="acc-stmt-body">
                    <div className="acc-stmt-field">
                        <label className="acc-stmt-label">Date Range</label>
                        <RadioGroup
                            className="acc-stmt-radio-group"
                            onChange={this.onChangeDateFilter}
                            value={dateFilter}
                        >
                            {DATE_RANGE_OPTIONS.map(opt => (
                                <Radio.Button key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Radio.Button>
                            ))}
                        </RadioGroup>
                        {dateFilter === "custom" && (
                            <RangePicker
                                className="acc-stmt-range"
                                disabledDate={value => value && value.valueOf() > moment().valueOf()}
                                onChange={this.onChangeCustomRange}
                            />
                        )}
                    </div>

                    <div className="acc-stmt-field">
                        <label className="acc-stmt-label">Filter By</label>
                        <Select
                            className="acc-stmt-select"
                            placeholder="Filter By"
                            value={filterBy}
                            onChange={this.onChangeFilterBy}
                        >
                            {FILTER_OPTIONS.map(opt => (
                                <Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div className="acc-stmt-field">
                        <label className="acc-stmt-label">Select ID</label>
                        <Select
                            className="acc-stmt-select"
                            placeholder="Select ID"
                            value={itemId}
                            disabled={!filterBy}
                            onChange={this.onChangeItemId}
                        >
                            {this.idOptions.map(id => (
                                <Option key={id} value={id}>
                                    {id}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div className="acc-stmt-field">
                        <label className="acc-stmt-label">Report</label>
                        <div className="acc-stmt-report-pill">
                            <Icon type="file-pdf" /> Account Statement
                        </div>
                    </div>
                </div>

                <div className="acc-stmt-footer">
                    <Button onClick={onClose} className="acc-stmt-cancel">
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        icon="download"
                        loading={downloading}
                        onClick={this.handleDownload}
                        className="acc-stmt-download"
                    >
                        Download
                    </Button>
                </div>
            </Drawer>
        );
    }
}

AccountStatementDrawer.propTypes = {
    visible: PropTypes.bool,
    onClose: PropTypes.func,
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    caseIds: PropTypes.array,
    buyerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default AccountStatementDrawer;
